import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listingsApi } from '../lib/api';
import ListingCard from '../components/ListingCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Search, Filter, X, MapPin, Loader2, SlidersHorizontal } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const listingTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'stall', label: 'Stall' },
  { value: 'pasture', label: 'Pasture' },
  { value: 'layover', label: 'Layover' },
  { value: 'full_boarding', label: 'Full Boarding' },
];

const speciesOptions = [
  { value: 'all', label: 'All Species' },
  { value: 'horses', label: 'Horses' },
  { value: 'cattle', label: 'Cattle' },
  { value: 'sheep', label: 'Sheep' },
  { value: 'goats', label: 'Goats' },
];

export default function SearchListings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    state: searchParams.get('state') || '',
    type: searchParams.get('type') || 'all',
    species: searchParams.get('species') || 'all',
    capacity: parseInt(searchParams.get('capacity')) || 1,
    priceRange: [0, 500],
  });

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        status: 'approved',
        ...(filters.city && { city: filters.city }),
        ...(filters.state && { state: filters.state }),
        ...(filters.type !== 'all' && { listing_type: filters.type }),
        ...(filters.species !== 'all' && { species: filters.species }),
        ...(filters.capacity > 1 && { capacity: filters.capacity }),
        ...(filters.priceRange[0] > 0 && { min_price: filters.priceRange[0] }),
        ...(filters.priceRange[1] < 500 && { max_price: filters.priceRange[1] }),
        ...params,
      };
      const response = await listingsApi.getAll(queryParams);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings();
    // Update URL params
    const params = new URLSearchParams();
    if (filters.city) params.set('city', filters.city);
    if (filters.state) params.set('state', filters.state);
    if (filters.type !== 'all') params.set('type', filters.type);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      state: '',
      type: 'all',
      species: 'all',
      capacity: 1,
      priceRange: [0, 500],
    });
    setSearchParams({});
    fetchListings({});
  };

  const hasActiveFilters = 
    filters.city || 
    filters.state || 
    filters.type !== 'all' || 
    filters.species !== 'all' ||
    filters.capacity > 1 ||
    filters.priceRange[0] > 0 ||
    filters.priceRange[1] < 500;

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="search-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-2">
            Find Stables & Pastures
          </h1>
          <p className="text-farmhouse-muted">
            Browse verified listings for your horses and livestock
          </p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
              <Input
                type="text"
                placeholder="City..."
                value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                className="form-input pl-12"
                data-testid="search-city"
              />
            </div>
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="State..."
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                className="form-input"
                data-testid="search-state"
              />
            </div>
            <Select
              value={filters.type}
              onValueChange={(value) => setFilters({ ...filters, type: value })}
            >
              <SelectTrigger className="w-full md:w-48 form-input" data-testid="search-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-farmhouse-paper border-farmhouse-subtle">
                {listingTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value} className="text-farmhouse-cream hover:bg-farmhouse-subtle">
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" className="btn-primary" data-testid="search-submit">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary md:w-auto"
              data-testid="toggle-filters"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </form>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mb-8 p-6 bg-farmhouse-paper border border-farmhouse-subtle rounded-md animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg font-semibold text-farmhouse-cream">Filters</h3>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-farmhouse-muted hover:text-farmhouse-cream"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-farmhouse-cream">Species</Label>
                <Select
                  value={filters.species}
                  onValueChange={(value) => setFilters({ ...filters, species: value })}
                >
                  <SelectTrigger className="form-input" data-testid="filter-species">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-farmhouse-paper border-farmhouse-subtle">
                    {speciesOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-farmhouse-cream hover:bg-farmhouse-subtle">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-farmhouse-cream">Min Capacity: {filters.capacity}</Label>
                <Slider
                  value={[filters.capacity]}
                  onValueChange={(value) => setFilters({ ...filters, capacity: value[0] })}
                  min={1}
                  max={20}
                  step={1}
                  className="py-2"
                  data-testid="filter-capacity"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-farmhouse-cream">
                  Price Range: {formatCurrency(filters.priceRange[0])} - {formatCurrency(filters.priceRange[1])}
                </Label>
                <Slider
                  value={filters.priceRange}
                  onValueChange={(value) => setFilters({ ...filters, priceRange: value })}
                  min={0}
                  max={500}
                  step={10}
                  className="py-2"
                  data-testid="filter-price"
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-farmhouse-subtle">
              <Button onClick={handleSearch} className="btn-primary" data-testid="apply-filters">
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-farmhouse-muted">
            {loading ? 'Searching...' : `${listings.length} listings found`}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="listings-grid">
            {listings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-farmhouse-paper border border-farmhouse-subtle rounded-md">
            <Search className="w-16 h-16 mx-auto mb-4 text-farmhouse-subtle" />
            <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-2">
              No listings found
            </h3>
            <p className="text-farmhouse-muted mb-4">
              Try adjusting your search criteria or browse all listings
            </p>
            <Button onClick={clearFilters} className="btn-secondary">
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
